/**
 * Editor Store
 *
 * Zustand store for editor state:
 * - Content and dirty state
 * - Save status
 * - Cursor position
 * - Validation results
 * - Mode (edit/diff/readonly)
 */

import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import { configApi } from "../api/configApi";
import type { EditorMode, SaveStatus, ValidationResult, ValidationIssue } from "../types";

interface CursorPosition {
  readonly line: number;
  readonly column: number;
}

interface EditorState {
  // Content
  readonly currentContent: string;
  readonly originalContent: string;
  readonly isDirty: boolean;

  // Mode
  readonly mode: EditorMode;

  // Save status
  readonly saveStatus: SaveStatus;
  readonly lastSavedAt: string | undefined;

  // Cursor
  readonly cursorPosition: CursorPosition | undefined;

  // Validation
  readonly validationResult: ValidationResult | undefined;
  readonly isValidating: boolean;

  // Actions
  readonly setContent: (content: string) => void;
  readonly setOriginalContent: (content: string) => void;
  readonly setCursorPosition: (position: CursorPosition) => void;
  readonly setValidationResult: (result: ValidationResult | undefined) => void;
  readonly setMode: (mode: EditorMode) => void;
  readonly toggleDiffMode: () => void;
  readonly save: () => Promise<void>;
  readonly formatDocument: () => void;
  readonly validate: () => void;
  readonly reset: () => void;
}

const initialState = {
  currentContent: "",
  originalContent: "",
  isDirty: false,
  mode: "edit" as EditorMode,
  saveStatus: "idle" as SaveStatus,
  lastSavedAt: undefined,
  cursorPosition: undefined,
  validationResult: undefined,
  isValidating: false,
};

export const useEditorStore = create<EditorState>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    setContent: (content: string) => {
      const { originalContent } = get();
      set({
        currentContent: content,
        isDirty: content !== originalContent,
        saveStatus: "idle",
      });
    },

    setOriginalContent: (content: string) => {
      set({
        originalContent: content,
        currentContent: content,
        isDirty: false,
        saveStatus: "idle",
      });
    },

    setCursorPosition: (position: CursorPosition) => {
      set({ cursorPosition: position });
    },

    setValidationResult: (result: ValidationResult | undefined) => {
      set({ validationResult: result, isValidating: false });
    },

    setMode: (mode: EditorMode) => {
      set({ mode });
    },

    toggleDiffMode: () => {
      const { mode } = get();
      set({ mode: mode === "diff" ? "edit" : "diff" });
    },

    save: async () => {
      const { currentContent, isDirty, saveStatus } = get();

      if (!isDirty || saveStatus === "saving") {
        return;
      }

      set({ saveStatus: "saving" });

      try {
        // Get current path from config store (imported dynamically to avoid circular deps)
        const { useConfigStore } = await import("./configStore");
        const { currentPath, metadata } = useConfigStore.getState();

        if (!currentPath || !metadata) {
          throw new Error("No config selected");
        }

        // Save via API
        const result = await configApi.updateConfig({
          path: currentPath,
          content: currentContent,
          configType: metadata.configType,
          verticalKey: metadata.verticalKey,
        });

        if (!result.success) {
          throw new Error(result.error.message);
        }

        const now = new Date().toISOString();
        set({
          saveStatus: "saved",
          lastSavedAt: now,
          originalContent: currentContent,
          isDirty: false,
        });

        // Reset save status after 2 seconds
        setTimeout(() => {
          const state = get();
          if (state.saveStatus === "saved") {
            set({ saveStatus: "idle" });
          }
        }, 2000);
      } catch (error) {
        console.error("Save failed:", error);
        set({ saveStatus: "error" });

        // Reset error status after 3 seconds
        setTimeout(() => {
          const state = get();
          if (state.saveStatus === "error") {
            set({ saveStatus: "idle" });
          }
        }, 3000);
      }
    },

    formatDocument: () => {
      const { currentContent, setContent } = get();

      try {
        const parsed = JSON.parse(currentContent);
        const formatted = JSON.stringify(parsed, null, 2);
        setContent(formatted);
      } catch {
        // Cannot format invalid JSON
      }
    },

    validate: () => {
      const { currentContent, setValidationResult } = get();
      set({ isValidating: true });

      // Basic JSON validation
      const issues: ValidationIssue[] = [];

      try {
        JSON.parse(currentContent);
      } catch (error) {
        const parseError = error as SyntaxError;
        issues.push({
          id: `parse-error-${Date.now()}`,
          severity: "error",
          message: parseError.message,
          path: "",
        });
      }

      const result: ValidationResult = {
        isValid: issues.length === 0,
        issues,
        validatedAt: new Date().toISOString(),
      };

      setValidationResult(result);
    },

    reset: () => {
      set(initialState);
    },
  }))
);

// Subscribe to content changes for auto-validation
useEditorStore.subscribe(
  (state) => state.currentContent,
  (content) => {
    if (content) {
      // Debounced validation will be triggered by useEditor hook
    }
  }
);
